<script
  lang="ts"
  setup
  generic="T, TMulti extends boolean | undefined, TValue extends (TMulti extends true ? T[] : T)"
>
import { VButton } from '@shared/component/VButton';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import VSpinner from '@shared/component/VSpinner/VSpinner.vue';
import VTag from '@shared/component/VTag.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { checkIfNumber, objectsAreEqual, search } from '@tools/helpers';
import { isEnterKey } from '@tools/keyboard';

import { computed, onMounted, onUnmounted, ref, unref, watch, useTemplateRef } from 'vue';
import { useId } from '@tools/composables/id';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import BulkField from '../BulkField/BulkField.vue';
import FieldRowV2 from '../FieldRowV2/FieldRowV2.vue';
import { type InputOption, type InputOptionGroup, type UpdateEventOf } from '../types';

const props = withDefaults(
  defineProps<{
    id?: string;
    dataTestid?: string;
    label?: string;
    path?: string;
    value?: TValue | null;
    placeholder?: string;
    loading?: boolean;
    options?: InputOption<T>[];
    optionGroups?: InputOptionGroup<T>[];
    disabled?: boolean;
    readonly?: boolean;
    multi?: TMulti | boolean;
    emptyOption?: boolean;
    withSearch?: boolean;
    searchThrottle?: number;
    allowBulk?: boolean;
    hasError?: boolean;
  }>(),
  {
    dataTestid: 'SelectField',
    placeholder: 'maak een keuze..',
    loading: false,
    options: () => [],
    optionGroups: () => [],
    disabled: false,
    readonly: false,
    emptyOption: false,
    withSearch: false,
    searchThrottle: 0,
    allowBulk: true,
    hasError: false,
    multi: false,
  },
);
const emit = defineEmits<{
  updateField: [value: UpdateEventOf<TValue>];
  'update:value': [value: TValue];
  blur: [];
}>();

const bulkErrorList = ref<string[]>([]);
const root = useTemplateRef<HTMLDivElement>('root');
const vsearch = useTemplateRef<HTMLDivElement>('vsearch');
const val = ref<InputOption<T>>();
const multiVal = ref<InputOption<T>[]>([]);

const searchTerm = ref();
const clearSearch = ref(false);
const focusSearch = ref(false);
const isOpen = ref(false);

const currentSelectIndex = ref<number>();

const ids = useId('select-field', ['listbox']);
const activeDescendantId = computed(() =>
  currentSelectIndex.value !== undefined
    ? `${ids.listbox}-option-${currentSelectIndex.value}`
    : undefined,
);

const showModal = ref(false);
const allOptions = ref<InputOption<T>[]>([]);

const showSearch = computed(() => allOptions.value.length > 10 || props.withSearch);

const defaultOptions = computed(() => {
  if (searchTerm.value && searchTerm.value.length >= props.searchThrottle) {
    return props.options.filter((d) => search(d, searchTerm.value));
  }
  return props.options;
});

const groupOptions = computed(() => {
  if (searchTerm.value) {
    return props.optionGroups?.reduce(
      (arr: Array<InputOptionGroup<T>>, group: InputOptionGroup<T>) => {
        const filteredItems = group.items.filter((d: InputOption<T>) =>
          search(d, searchTerm.value),
        );
        if (filteredItems.length)
          arr.push({ label: filteredItems.length ? group.label : '', items: filteredItems });
        return arr;
      },
      [],
    );
  }

  return props.optionGroups;
});

watch(
  () => isOpen.value,
  (value) => {
    focusSearch.value = value;
  },
);

watch(
  () => [props.value, props.options, props.optionGroups],

  ([value, opts, grps]) => {
    allOptions.value = (opts as InputOption<T>[])?.length
      ? checkIfStrings(opts as InputOption<T>[])
      : (grps as InputOptionGroup<T>[])?.length
        ? (grps as InputOptionGroup<T>[]).reduce(
            (acc: Array<InputOption<T>>, curVal: InputOptionGroup<T>) => [...acc, ...curVal.items],
            [],
          )
        : [];

    if (value) {
      if (props.multi) {
        const uniqueVals = [...new Set(value as TValue[])];
        multiVal.value = uniqueVals
          .map((item) => {
            const found = allOptions.value.find((option) => option.value === item);
            if (found) {
              return { label: found.label, value: found.value };
            }
          })
          .filter((item) => item) as Array<InputOption<T>>;
      } else {
        // @ts-expect-error: See vue issue #6766 generic refs
        val.value = allOptions.value.find(
          (item) =>
            checkIfNumber(value) === checkIfNumber(item.value) ||
            // @ts-expect-error: See vue issue #6766 generic refs
            (typeof item.value === 'object' && objectsAreEqual(item.value, value)),
        );
      }
    } else {
      val.value = undefined;
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

function checkIfStrings(optns: Array<InputOption<T> | string>) {
  if (optns.every((elem: InputOption<T> | string) => typeof elem === 'string')) {
    return (optns as unknown as string[]).map((item: string) => ({
      label: item,
      value: item,
    })) as unknown as Array<InputOption<T>>;
  }
  return optns as Array<InputOption<T>>;
}

function updateField(value: string[]) {
  bulkErrorList.value = [];
  multiVal.value = [];
  if (value.length === 1) {
    if (value[0] === '') {
      onSelectEmpty();
      return;
    }
  }
  value.forEach((v) => {
    const labelValuePair = props.options.find((o) => String(o.value) === String(v));
    if (!labelValuePair) {
      bulkErrorList.value.push(v.toString());
      return;
    }
    onSelect(labelValuePair, false);
  });
}

function toggleList() {
  isOpen.value = !isOpen.value;
  clearSearch.value = !clearSearch.value;
}

function onClick() {
  toggleList();
}

function onBlur() {
  if (!showSearch.value) isOpen.value = false;
  emit('blur');
}

function fetchKeyboardEvent(event: KeyboardEvent) {
  if (isEnterKey(event)) stopPropagation(event);

  if (event.type === 'keydown' && event.code === 'ArrowDown') stopPropagation(event);
  if (event.type === 'keydown' && event.code === 'ArrowUp') stopPropagation(event);

  if (event.type === 'keyup' && event.code === 'Tab') isOpen.value = true;
  if (event.type === 'keyup' && event.code === 'ArrowDown') onDown();
  if (event.type === 'keyup' && event.code === 'ArrowUp') onUp();
  if (event.type === 'keyup' && isEnterKey(event)) onEnter();
}

// make sure the page does not scroll through pressing key up/down space bar when the list is open
function stopPropagation(event: Event) {
  event.preventDefault();
  event.stopPropagation();
}

function onSelect(event: InputOption<T>, stayOpen: boolean = true) {
  if (props.multi) {
    if (multiVal.value.some((v) => String(v.value) === String(event.value))) return;
    // @ts-expect-error: See vue issue #6766 generic refs
    multiVal.value.push({ label: event.label, value: event.value });
  } else {
    val.value = event;
  }

  // @ts-expect-error: See vue issue #6766 generic refs
  const value: T | T[] = props.multi
    ? multiVal.value.map((item) => unref(item.value))
    : event.value;
  if (stayOpen) isOpen.value = props.multi ?? false;
  // @ts-expect-error: See vue issue #6766 generic refs
  emit('updateField', { path: props.path, value });
  // @ts-expect-error: See vue issue #6766 generic refs
  emit('update:value', value);
}

function onSelectEmpty() {
  val.value = undefined;
  isOpen.value = false;
  const value = props.multi ? [] : '';
  emit('updateField', { path: props.path, value: value as TValue });
  emit('update:value', value as TValue);
}

function setSearchTerm(event: string) {
  searchTerm.value = event;
  currentSelectIndex.value = undefined;
}

function removeTag(tag: InputOption<T>) {
  multiVal.value = multiVal.value?.filter((item) => item.value !== tag.value);

  // @ts-expect-error: See vue issue #6766 generic refs
  const value: TValue = multiVal.value.map((item) => item.value);
  emit('updateField', { path: props.path, value });
  emit('update:value', value);
}

function handleClickOutside(evt: MouseEvent) {
  evt.stopPropagation();
  if (!(evt.target instanceof Node)) return;

  if (!root.value?.contains(evt.target)) {
    isOpen.value = false;
    return;
  }
  if (vsearch.value?.contains(evt.target)) {
    isOpen.value = true;
  }
}

function toggleModal() {
  showModal.value = !showModal.value;
}

function closeModal() {
  showModal.value = false;
  bulkErrorList.value = [];
}

onMounted(() => {
  if (!props.readonly) document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

function onUp() {
  if (currentSelectIndex.value === undefined) {
    currentSelectIndex.value = 0;
  }
  if (currentSelectIndex.value > 0) {
    currentSelectIndex.value--;
  }
  const el = document.querySelector(
    `#${CSS.escape(`${ids.listbox}-option-${currentSelectIndex.value}`)}`,
  );
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
}

function onDown() {
  if (currentSelectIndex.value === undefined) {
    currentSelectIndex.value = 0;
  } else {
    currentSelectIndex.value++;
  }
  // if there are groups, we need to get the total number of items within the groups
  if (props.optionGroups.length > 1) {
    let totalCount = 0;
    groupOptions.value.forEach((go) => (totalCount += go.items.length));
    if (currentSelectIndex.value >= totalCount) {
      currentSelectIndex.value = totalCount - 1;
    }
  } else if (currentSelectIndex.value >= defaultOptions.value.length - 1) {
    currentSelectIndex.value = defaultOptions.value.length - 1;
  }
  const el = document.querySelector(
    `#${CSS.escape(`${ids.listbox}-option-${currentSelectIndex.value}`)}`,
  );
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
}

function onEnter() {
  if (currentSelectIndex.value !== undefined) {
    if (props.optionGroups.length > 1) {
      const item = groupOptions.value.flatMap((go) => go.items)[currentSelectIndex.value];
      onSelect(item);
      if (!props.multi) isOpen.value = false;
    } else {
      const item = defaultOptions.value[currentSelectIndex.value];
      onSelect(item);
      if (!props.multi) isOpen.value = false;
    }
  }
}

function getTotalIndex(index: number, groupIndex: number) {
  const groupsBefore = groupOptions.value.slice(0, groupIndex);
  let total: number = 0;
  groupsBefore.forEach((go) => (total += go.items.length));
  return total + index;
}
</script>

<template>
  <!-- eslint-disable vuejs-accessibility/interactive-supports-focus de oplossing van deze rule gaf andere problemen in deze file daarom uitgezet -->
  <div
    :class="[
      'flex min-h-form-row min-w-0 flex-1 items-center',
      {
        error: hasError,
        filled: (props.multi && multiVal.length) || (!props.multi && val) || placeholder,
      },
    ]"
  >
    <div
      v-if="!readonly"
      class="styled-input select h-auto! after:pointer-events-none after:absolute after:top-1/2 after:right-[1.125em] after:z-4 after:m-0 after:block after:aspect-square after:em-w-2.5 after:origin-center after:-translate-y-3/4 after:-rotate-45 after:rounded-xs after:border-[3px] after:border-t-0 after:border-r-0 after:border-solid motion-safe:transition motion-safe:after:transition-colors"
      :class="{
        disabled,
        'cursor-pointer': !props.disabled,
        'border-b-red bg-red/10 after:border-red': props.hasError,
        'cursor-not-allowed after:border-grey-600': props.disabled,
        'after:border-primary hover:bg-primary-200 focus:bg-primary-200':
          !props.hasError && !props.disabled,
        'after:z-[-1]': props.loading,
      }"
      :data-testid="props.dataTestid"
      :id="props.id"
      ref="root"
      @click="onClick"
      @blur="onBlur"
      @keyup="fetchKeyboardEvent"
      @keydown="fetchKeyboardEvent"
      tabindex="0"
      role="combobox"
      :aria-expanded="isOpen && !props.disabled"
      aria-haspopup="listbox"
      :aria-controls="ids.listbox"
      :aria-activedescendant="activeDescendantId"
      :aria-disabled="props.disabled ? true : undefined"
      :aria-labelledby="props.id ? `${props.id}-label` : undefined"
    >
      <div v-if="multi" class="m-[-.1875rem] flex flex-wrap">
        <VTag
          v-for="(tag, index) in multiVal"
          :key="index"
          :label="tag.label"
          @remove="removeTag(tag as InputOption<T>)"
          :disabled="disabled"
        />
        <div v-if="!multiVal.length" class="text-grey-400">{{ placeholder }}</div>
      </div>

      <div v-else class="max-w-[calc(100%-2rem)] truncate" data-testid="selected-label">
        <template v-if="val">
          {{ val.label }}
        </template>

        <span v-else class="text-grey-400">
          {{ placeholder || '&nbsp;' }}
        </span>
      </div>
      <VSpinner v-if="loading" />
      <button
        v-if="!loading && !disabled"
        type="button"
        tabindex="-1"
        aria-label="toggle"
        @click.stop="toggleList"
        class="absolute top-0 right-0 z-5 flex h-full w-10 cursor-pointer items-center justify-center bg-transparent"
      ></button>

      <div
        v-if="!disabled"
        class="absolute top-full right-0 z-widget w-full flex-col bg-white p-5 shadow"
        :class="{
          flex: isOpen,
          hidden: !isOpen,
        }"
        data-testid="SelectField__popup"
      >
        <div v-if="showSearch" ref="vsearch" class="em-mb-4" data-testid="search-box">
          <VSearch
            :placeholder="
              searchThrottle ? `type hier minimaal ${searchThrottle} tekens` : 'zoeken...'
            "
            :clear="clearSearch"
            :on-focus="focusSearch"
            @input="setSearchTerm"
            @onTab="toggleList"
          />
        </div>
        <div
          v-if="options.length"
          :id="ids.listbox"
          role="listbox"
          class="flex max-h-72 flex-col overflow-x-hidden overflow-y-auto"
        >
          <span
            v-if="emptyOption"
            @click.stop="onSelectEmpty"
            role="option"
            :aria-selected="!val"
            class="cursor-pointer em-pl-2 leading-relaxed text-grey hover:bg-primary hover:text-white"
          >
            {{ placeholder || 'Maak een keuze...' }}
          </span>
          <span
            v-for="(option, index) in defaultOptions"
            :id="`${ids.listbox}-option-${index}`"
            :data-testid="
              ['non-grouped-option', ...(option.disabled ? ['disabled-option'] : [])].join(' ')
            "
            :key="index"
            @click.stop="!option.disabled && onSelect(option)"
            role="option"
            :aria-selected="option.value === val?.value"
            :aria-disabled="option.disabled ? true : undefined"
            class="flex cursor-pointer flex-nowrap items-baseline gap-1 em-pl-2 leading-relaxed"
            :class="{
              'text-grey hover:cursor-not-allowed hover:bg-grey-200': option.disabled,
              'hover:bg-primary hover:text-white': !option.disabled && option.value !== val?.value,
              'bg-primary-300': option.value === val?.value,
              'bg-primary text-white': index === currentSelectIndex,
            }"
          >
            <span>{{ option.label }}</span>
            <span class="truncate text-xs text-grey-400" v-if="!!option.description"
              >({{ option.description }})</span
            >
          </span>
          <span v-if="!defaultOptions.length">Geen resultaten gevonden</span>
        </div>

        <div
          v-if="optionGroups.length"
          :id="ids.listbox"
          role="listbox"
          class="flex max-h-72 flex-col overflow-x-hidden overflow-y-auto"
        >
          <span
            v-if="emptyOption"
            @click.stop="onSelectEmpty"
            role="option"
            :aria-selected="!val"
            class="cursor-pointer leading-relaxed text-grey hover:bg-primary hover:text-white"
          >
            {{ placeholder || 'Maak een keuze...' }}
          </span>
          <div
            data-testid="option-group"
            v-for="(group, groupIndex) in groupOptions"
            :key="groupIndex"
            role="group"
            :aria-label="group.label"
            class="flex flex-col flex-wrap"
          >
            <strong class="pointer-events-none">{{ group.label }}</strong>
            <span
              :id="`${ids.listbox}-option-${getTotalIndex(index, groupIndex)}`"
              :data-testid="
                ['grouped-option', ...(option.disabled ? ['disabled-option'] : [])].join(' ')
              "
              v-for="(option, index) in group.items"
              :key="index"
              @click.stop="!option.disabled && onSelect(option)"
              role="option"
              :aria-selected="option.value === val?.value"
              :aria-disabled="option.disabled ? true : undefined"
              class="cursor-pointer em-pl-2 leading-relaxed"
              :class="{
                'hover:cursor-not-allowed hover:bg-grey-200': option.disabled,
                'bg-primary-300': option.value === val?.value,
                'hover:bg-primary hover:text-white': option.value !== val?.value,
                'bg-primary text-white': getTotalIndex(index, groupIndex) === currentSelectIndex,
              }"
            >
              {{ option.label }}
            </span>
          </div>
          <span v-if="!groupOptions.length">Geen resultaten gevonden</span>
        </div>
      </div>
    </div>
    <div v-else class="filled group-[&]/float-label:h-11 group-[&]/float-label:content-center">
      {{ val ? val.label : '-' }}
    </div>
    <VTooltip v-if="multi && allowBulk" label="voer een lijst in, gescheiden door komma's">
      <VButton v-if="allowBulk" class="ml-3" :disabled="disabled" inverted @click="toggleModal">
        <FontAwesomeIcon icon="fa-light fa-ellipsis-v-alt" />
      </VButton>
    </VTooltip>
    <VModal
      overflow
      v-if="showModal"
      confirm-button-text=""
      cancel-button-text="Sluiten"
      :is-active="showModal"
      @close-modal="closeModal"
    >
      <FieldRowV2
        tooltip="Voer een lijst met waarden in, gescheiden door komma's. Ongeldige waarden worden er automatisch uitgefilterd."
        :label="label"
      >
        <BulkField
          :path="path"
          :value="value as any"
          placeholder="Voer een lijst met waarden in, gescheiden door komma's."
          @update:value="(e) => updateField(e as string[])"
        />
      </FieldRowV2>
      <VInfoBox v-if="bulkErrorList.length" type="danger">
        <p>De volgende waarden zijn niet gevonden, en zijn niet meegenomen:</p>
        <span v-for="(item, index) in bulkErrorList" :key="index">
          <span v-if="index">, </span>
          <strong>{{ item }}</strong>
        </span>
      </VInfoBox>
    </VModal>
  </div>
  <!-- eslint-enable -->
</template>
